import { apiClient, ApiError } from "@/lib/api";
import {
  Boat,
  BoatImage,
  BoatLoan,
  BoatLoanWriteBody,
  BoatWriteBody,
  ListingState,
} from "@/types/inventory";

// Sold-date window for the sold tab (YYYY-MM-DD, inclusive), mapped to the
// backend's sold_from/sold_to query params.
export interface SoldDateRange {
  from?: string
  to?: string
}

export const fetchBoats = async (
  listingState?: ListingState,
  q?: string,
  excludeListingState?: ListingState,
  sold?: SoldDateRange,
): Promise<Boat[]> => {
  const params: Record<string, string> = {};
  if (listingState) params.listing_state = listingState;
  if (q) params.q = q;
  if (excludeListingState) params.exclude_listing_state = excludeListingState;
  if (sold?.from) params.sold_from = sold.from;
  if (sold?.to) params.sold_to = sold.to;
  return apiClient<Boat[]>("/inventory/boats", {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
};

export const fetchBoatById = async (id: string): Promise<Boat> => {
  return apiClient<Boat>(`/inventory/boats/${id}`);
};

export const createBoat = async (body: BoatWriteBody): Promise<Boat> =>
  apiClient<Boat>("/inventory/boats", { method: "POST", body });

export const updateBoat = async (id: string, body: BoatWriteBody): Promise<Boat> =>
  apiClient<Boat>(`/inventory/boats/${id}`, { method: "PUT", body });

export const deleteBoat = async (id: string): Promise<void> =>
  apiClient<void>(`/inventory/boats/${id}`, { method: "DELETE" });

// --- Floor-plan loans ---

// 404 means "no active loan" — translate to null so the boat page can render
// an empty state without surfacing an error.
export const fetchLoanByBoat = async (boatId: string): Promise<BoatLoan | null> => {
  try {
    return await apiClient<BoatLoan>(`/inventory/boats/${boatId}/loan`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
};

export const createBoatLoan = async (
  boatId: string,
  body: BoatLoanWriteBody,
): Promise<BoatLoan> =>
  apiClient<BoatLoan>(`/inventory/boats/${boatId}/loan`, { method: "POST", body });

export const updateBoatLoan = async (
  loanId: string,
  body: BoatLoanWriteBody,
): Promise<BoatLoan> =>
  apiClient<BoatLoan>(`/inventory/loans/${loanId}`, { method: "PUT", body });

export const deleteBoatLoan = async (loanId: string): Promise<void> =>
  apiClient<void>(`/inventory/loans/${loanId}`, { method: "DELETE" });

export const payoffBoatLoan = async (
  loanId: string,
  body: { paid_off_on?: string; payoff_amount?: number },
): Promise<BoatLoan> =>
  apiClient<BoatLoan>(`/inventory/loans/${loanId}/payoff`, { method: "POST", body });

export const payCurtailment = async (
  loanId: string,
  curtailmentId: string,
  paidOn?: string,
): Promise<BoatLoan> =>
  apiClient<BoatLoan>(`/inventory/loans/${loanId}/curtailments/${curtailmentId}/pay`, {
    method: "POST",
    body: paidOn ? { paid_on: paidOn } : {},
  });

export const unpayCurtailment = async (
  loanId: string,
  curtailmentId: string,
): Promise<BoatLoan> =>
  apiClient<BoatLoan>(`/inventory/loans/${loanId}/curtailments/${curtailmentId}/unpay`, {
    method: "POST",
    body: {},
  });

export const payInterestCharge = async (
  loanId: string,
  chargeId: string,
  paidOn?: string,
): Promise<BoatLoan> =>
  apiClient<BoatLoan>(`/inventory/loans/${loanId}/interest/${chargeId}/pay`, {
    method: "POST",
    body: paidOn ? { paid_on: paidOn } : {},
  });

export const unpayInterestCharge = async (
  loanId: string,
  chargeId: string,
): Promise<BoatLoan> =>
  apiClient<BoatLoan>(`/inventory/loans/${loanId}/interest/${chargeId}/unpay`, {
    method: "POST",
    body: {},
  });

// --- Boat images ---

export const fetchBoatImages = async (boatId: string): Promise<BoatImage[]> => {
  const data = await apiClient<BoatImage[] | null>(`/inventory/boats/${boatId}/images`);
  return data ?? [];
};

// Parallel photo uploads. Matches the backend's per-tenant connection pool
// (MaxConns=4) — more would just queue server-side.
export const UPLOAD_CONCURRENCY = 4;

export const uploadBoatImage = async (
  boatId: string,
  file: File,
  sortOrder?: number,
): Promise<BoatImage> => {
  const form = new FormData();
  form.append("file", file);
  if (sortOrder !== undefined) form.append("sort_order", String(sortOrder));
  return apiClient<BoatImage>(`/inventory/boats/${boatId}/images`, { method: "POST", body: form });
};

export const updateBoatImage = async (
  boatId: string,
  imageId: string,
  body: { is_cover?: boolean; sort_order?: number; title?: string; category?: string },
): Promise<BoatImage> =>
  apiClient<BoatImage>(`/inventory/boats/${boatId}/images/${imageId}`, { method: "PUT", body });

export const deleteBoatImage = async (boatId: string, imageId: string): Promise<void> =>
  apiClient<void>(`/inventory/boats/${boatId}/images/${imageId}`, { method: "DELETE" });

// --- Cross-module read-throughs ---
// Inventory stays decoupled from storage/service: the frontend composes
// them in parallel on the boat detail page instead of JOINing server-side.

export interface SlipBrief {
  id: string;
  slip_number: string;
  type: string;
  max_length_ft: number;
  status: string;
  current_boat_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkOrderBrief {
  id: string;
  boat_id: string;
  boat_name: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  assigned_technician?: string;
  created_at: string;
  updated_at: string;
}

// 404 means "no slip assigned" — translate to null so callers can render an
// empty state without surfacing an error.
export const fetchSlipForBoat = async (boatId: string): Promise<SlipBrief | null> => {
  try {
    return await apiClient<SlipBrief>("/storage/slips/by-boat", { params: { boat_id: boatId } });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
};

export const fetchWorkOrdersForBoat = async (boatId: string): Promise<WorkOrderBrief[]> => {
  const data = await apiClient<WorkOrderBrief[] | null>("/service/work-orders", {
    params: { boat_id: boatId },
  });
  return data ?? [];
};
